import { Fragment, useState, type ReactNode } from 'react';
import type { AppState } from '../store/store';

/**
 * A small Markdown dialect for chat: **bold**, *italic*, __underline__,
 * ~~strike~~, ||spoiler||, `code`, ``` fenced code ```, > quotes, # headings,
 * - lists, links and mentions. It builds React elements directly, so user content is never
 * interpreted as HTML.
 */
export type MdNode =
  | { t: 'text'; v: string }
  | { t: 'br' }
  | { t: 'bold' | 'italic' | 'underline' | 'strike' | 'spoiler'; c: MdNode[] }
  | { t: 'code'; v: string }
  | { t: 'codeblock'; lang: string; v: string }
  | { t: 'quote'; c: MdNode[] }
  | { t: 'heading'; level: 1 | 2 | 3; c: MdNode[] }
  | { t: 'list'; items: MdNode[][] }
  | { t: 'link'; href: string }
  | { t: 'user' | 'role' | 'channel'; id: string }
  | { t: 'everyone'; v: 'everyone' | 'here' };

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

interface Rule {
  re: RegExp;
  build: (m: RegExpExecArray) => MdNode;
}

const RULES: Rule[] = [
  { re: /`([^`\n]+)`/, build: (m) => ({ t: 'code', v: m[1]! }) },
  { re: new RegExp(`<@(${UUID})>`), build: (m) => ({ t: 'user', id: m[1]! }) },
  { re: new RegExp(`<@&(${UUID})>`), build: (m) => ({ t: 'role', id: m[1]! }) },
  { re: new RegExp(`<#(${UUID})>`), build: (m) => ({ t: 'channel', id: m[1]! }) },
  { re: /(?<![\w@])@(everyone|here)\b/, build: (m) => ({ t: 'everyone', v: m[1] as 'everyone' | 'here' }) },
  { re: /<(https?:\/\/[^\s<>]+)>|(https?:\/\/[^\s<>"'`]+[^\s<>"'`.,;:!?)\]}])/, build: (m) => ({ t: 'link', href: m[1] ?? m[2]! }) },
  { re: /\|\|([\s\S]+?)\|\|/, build: (m) => ({ t: 'spoiler', c: parseInline(m[1]!) }) },
  { re: /\*\*([\s\S]+?)\*\*/, build: (m) => ({ t: 'bold', c: parseInline(m[1]!) }) },
  { re: /__([\s\S]+?)__/, build: (m) => ({ t: 'underline', c: parseInline(m[1]!) }) },
  { re: /~~([\s\S]+?)~~/, build: (m) => ({ t: 'strike', c: parseInline(m[1]!) }) },
  { re: /\*(?!\s)([^*\n]+?)\*/, build: (m) => ({ t: 'italic', c: parseInline(m[1]!) }) },
  { re: /(?<![\p{L}\p{N}_])_(?!\s)([^_\n]+?)_(?![\p{L}\p{N}_])/u, build: (m) => ({ t: 'italic', c: parseInline(m[1]!) }) },
];

function pushText(out: MdNode[], text: string): void {
  if (!text) return;
  const lines = text.split('\n');
  lines.forEach((line, i) => {
    if (i > 0) out.push({ t: 'br' });
    if (line) {
      const last = out[out.length - 1];
      if (last?.t === 'text') last.v += line;
      else out.push({ t: 'text', v: line });
    }
  });
}

export function parseInline(text: string): MdNode[] {
  const out: MdNode[] = [];
  let rest = text;
  let guard = 0;
  while (rest && guard++ < 2000) {
    let best: { index: number; length: number; node: MdNode } | null = null;
    for (const rule of RULES) {
      const m = rule.re.exec(rest);
      if (m && (best === null || m.index < best.index)) {
        best = { index: m.index, length: m[0].length, node: rule.build(m) };
        if (m.index === 0) break;
      }
    }
    if (!best) {
      pushText(out, rest);
      break;
    }
    pushText(out, rest.slice(0, best.index));
    out.push(best.node);
    rest = rest.slice(best.index + best.length);
  }
  return out;
}

export function parseMarkdown(content: string): MdNode[] {
  const out: MdNode[] = [];
  const fence = /```([\w+-]*)\n?([\s\S]*?)```/g;
  let last = 0;
  for (const m of content.matchAll(fence)) {
    out.push(...parseBlocks(content.slice(last, m.index)));
    out.push({ t: 'codeblock', lang: m[1] ?? '', v: (m[2] ?? '').replace(/\n$/, '') });
    last = m.index + m[0].length;
  }
  out.push(...parseBlocks(content.slice(last)));
  // Trim leading/trailing line breaks around code blocks.
  while (out[0]?.t === 'br') out.shift();
  while (out[out.length - 1]?.t === 'br') out.pop();
  return out;
}

const isBlock = (n: MdNode | undefined) => n?.t === 'quote' || n?.t === 'heading' || n?.t === 'list';

function parseBlocks(text: string): MdNode[] {
  if (!text) return [];
  const out: MdNode[] = [];
  const lines = text.split('\n');
  let buffer: string[] = [];
  let quote: string[] = [];
  let list: string[] = [];
  const flushText = () => {
    if (buffer.length) {
      if (out.length && !isBlock(out[out.length - 1])) out.push({ t: 'br' });
      out.push(...parseInline(buffer.join('\n')));
    }
    buffer = [];
  };
  const flushQuote = () => {
    if (quote.length) out.push({ t: 'quote', c: parseInline(quote.join('\n')) });
    quote = [];
  };
  const flushList = () => {
    if (list.length) out.push({ t: 'list', items: list.map((item) => parseInline(item)) });
    list = [];
  };
  for (const line of lines) {
    const q = /^>\s?(.*)$/.exec(line);
    // "# Title", "## Section", "### Sub-section", and "- item" / "* item" lists.
    const h = /^(#{1,3})\s+(\S.*)$/.exec(line);
    const li = /^\s*[-*]\s+(\S.*)$/.exec(line);
    if (q) {
      flushText();
      flushList();
      quote.push(q[1]!);
    } else if (h) {
      flushText();
      flushQuote();
      flushList();
      out.push({ t: 'heading', level: h[1]!.length as 1 | 2 | 3, c: parseInline(h[2]!) });
    } else if (li) {
      flushText();
      flushQuote();
      list.push(li[1]!);
    } else {
      flushQuote();
      flushList();
      buffer.push(line);
    }
  }
  flushText();
  flushQuote();
  flushList();
  return out;
}

const EMOJI_ONLY = /^(?:\p{Extended_Pictographic}|\p{Emoji_Component}|️|‍|\s)+$/u;

export function isEmojiOnly(content: string): boolean {
  const trimmed = content.trim();
  if (!trimmed || trimmed.length > 60 || /[0-9#*]/.test(trimmed)) return false;
  return EMOJI_ONLY.test(trimmed);
}

/** Plain-text rendering for notifications and reply previews. */
export function plainText(content: string, state: Pick<AppState, 'users' | 'channels' | 'spaces'>): string {
  return content
    .replace(new RegExp(`<@(${UUID})>`, 'g'), (_m, id: string) => `@${state.users[id]?.displayName ?? 'unknown'}`)
    .replace(new RegExp(`<@&(${UUID})>`, 'g'), (_m, id: string) => {
      for (const s of Object.values(state.spaces)) {
        const role = s.roles.find((r) => r.id === id);
        if (role) return `@${role.name}`;
      }
      return '@role';
    })
    .replace(new RegExp(`<#(${UUID})>`, 'g'), (_m, id: string) => `#${state.channels[id]?.name ?? 'channel'}`)
    .replace(/\|\|[\s\S]+?\|\|/g, '[spoiler]')
    .replace(/```[\w+-]*\n?([\s\S]*?)```/g, '$1')
    .replace(/^#{1,3}\s+/gm, '')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .replace(/(\*\*|__|~~|`|\*)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface RenderContext {
  userLabel: (id: string) => string;
  roleLabel: (id: string) => { name: string; color: string | null } | null;
  channelLabel: (id: string) => string | null;
  onUser?: (id: string, el: Element) => void;
  onChannel?: (id: string) => void;
  isSelf?: (id: string) => boolean;
}

function Spoiler({ children }: { children: ReactNode }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <span
      className="spoiler px-0.5"
      data-revealed={revealed}
      role="button"
      tabIndex={revealed ? -1 : 0}
      aria-label={revealed ? undefined : 'Spoiler, press to reveal'}
      onClick={() => setRevealed(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') setRevealed(true);
      }}
    >
      {children}
    </span>
  );
}

function CodeBlock({ lang, value }: { lang: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="group/code relative my-1 max-w-full">
      <pre className="scroll-thin overflow-x-auto rounded-md border border-line bg-code p-3 font-mono text-[0.85em] leading-relaxed">
        {lang && <div className="mb-1 text-[0.7rem] uppercase tracking-wide text-faint">{lang}</div>}
        <code>{value}</code>
      </pre>
      <button
        type="button"
        className="absolute right-2 top-2 rounded border border-line bg-elevated px-2 py-0.5 text-xs text-muted opacity-0 transition group-hover/code:opacity-100 focus:opacity-100"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

const pill = 'rounded px-1 font-medium bg-mention-pill text-accent hover:underline cursor-pointer';

function renderNodes(nodes: MdNode[], ctx: RenderContext, keyPrefix = ''): ReactNode[] {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}${i}`;
    switch (node.t) {
      case 'text':
        return <Fragment key={key}>{node.v}</Fragment>;
      case 'br':
        return <br key={key} />;
      case 'bold':
        return <strong key={key} className="font-semibold">{renderNodes(node.c, ctx, `${key}.`)}</strong>;
      case 'italic':
        return <em key={key}>{renderNodes(node.c, ctx, `${key}.`)}</em>;
      case 'underline':
        return <u key={key}>{renderNodes(node.c, ctx, `${key}.`)}</u>;
      case 'strike':
        return <s key={key}>{renderNodes(node.c, ctx, `${key}.`)}</s>;
      case 'spoiler':
        return <Spoiler key={key}>{renderNodes(node.c, ctx, `${key}.`)}</Spoiler>;
      case 'code':
        return (
          <code key={key} className="rounded border border-line bg-code px-1 py-px font-mono text-[0.85em]">
            {node.v}
          </code>
        );
      case 'codeblock':
        return <CodeBlock key={key} lang={node.lang} value={node.v} />;
      case 'quote':
        return (
          <blockquote key={key} className="my-0.5 border-l-4 border-line-strong pl-3 text-fg/90">
            {renderNodes(node.c, ctx, `${key}.`)}
          </blockquote>
        );
      case 'heading': {
        const Tag = (['h1', 'h2', 'h3'] as const)[node.level - 1]!;
        const size = node.level === 1 ? 'text-2xl' : node.level === 2 ? 'text-xl' : 'text-lg';
        return (
          <Tag key={key} className={`${size} mb-0.5 mt-1.5 block font-bold leading-snug first:mt-0`}>
            {renderNodes(node.c, ctx, `${key}.`)}
          </Tag>
        );
      }
      case 'list':
        return (
          <ul key={key} className="my-0.5 list-disc whitespace-normal pl-6">
            {node.items.map((item, j) => (
              <li key={j}>{renderNodes(item, ctx, `${key}.${j}.`)}</li>
            ))}
          </ul>
        );
      case 'link':
        return (
          <a key={key} href={node.href} target="_blank" rel="noopener noreferrer nofollow ugc" className="md-link break-words text-link hover:underline">
            {node.href}
          </a>
        );
      case 'user':
        return (
          <span key={key} className={`${pill} ${ctx.isSelf?.(node.id) ? 'bg-mention text-warning' : ''}`} onClick={(e) => ctx.onUser?.(node.id, e.currentTarget)}>
            @{ctx.userLabel(node.id)}
          </span>
        );
      case 'role': {
        const role = ctx.roleLabel(node.id);
        return (
          <span key={key} className="rounded px-1 font-medium" style={role?.color ? { color: role.color, background: `${role.color}22` } : undefined}>
            @{role?.name ?? 'deleted-role'}
          </span>
        );
      }
      case 'channel': {
        const name = ctx.channelLabel(node.id);
        return (
          <span key={key} className={pill} onClick={() => name && ctx.onChannel?.(node.id)}>
            #{name ?? 'unknown-channel'}
          </span>
        );
      }
      case 'everyone':
        return (
          <span key={key} className="rounded bg-mention-pill px-1 font-medium text-accent">
            @{node.v}
          </span>
        );
      default:
        return null;
    }
  });
}

export function Markdown({ content, ctx }: { content: string; ctx: RenderContext }) {
  const nodes = parseMarkdown(content);
  return <>{renderNodes(nodes, ctx)}</>;
}

// http(s) links, or bare `www.` addresses (opened over https). Trailing punctuation stays outside the link.
const PLAIN_LINK_RE = /(https?:\/\/[^\s<>"'`]+[^\s<>"'`.,;:!?)\]}])|\b(www\.[^\s<>"'`]+[^\s<>"'`.,;:!?)\]}])/gi;

/** Plain text (no Markdown) with web links made clickable; used for profile bios. */
export function LinkifiedText({ text }: { text: string }) {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(PLAIN_LINK_RE)) {
    const start = m.index;
    if (start > last) out.push(text.slice(last, start));
    const href = m[1] ?? `https://${m[2]}`;
    out.push(
      <a key={start} href={href} target="_blank" rel="noopener noreferrer nofollow ugc" className="md-link text-link hover:underline">
        {m[0]}
      </a>,
    );
    last = start + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}
